import { context, trace } from '@opentelemetry/api'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { Resource } from '@opentelemetry/resources'
import { BatchSpanProcessor, WebTracerProvider, ParentBasedSampler, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-web'
import { onCLS, onINP, onLCP } from 'web-vitals'

const SERVICE_NAME = 'simpanforbiz-cms'
const MAX_EVENT_BYTES = 4096
const allowedEventNames = new Set(['web_vital', 'route_change', 'api_retry', 'api_request', 'browser_error', 'unhandled_rejection', 'download'])

let configured = false

const normalizedPath = (value) => {
  try {
    const url = new URL(value, window.location.origin)
    return url.pathname.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/ig, ':id').replace(/\/\d+(?=\/|$)/g, '/:id')
  } catch (_) {
    return '/unknown'
  }
}

export const propagationTargets = () => (process.env.VUE_APP_TRACE_PROPAGATION_ORIGINS || process.env.VUE_APP_API_URL || '')
  .split(',')
  .map((value) => value.trim())
  .filter(Boolean)
  .flatMap((value) => {
    try {
      const origin = new URL(value).origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return [new RegExp(`^${origin}(?:/|$)`)]
    } catch (_) { return [] }
  })

export const recordRumEvent = (name, attributes = {}) => {
  if (process.env.VUE_APP_OBSERVABILITY_ENABLED === 'false') return
  if (!allowedEventNames.has(name)) return
  const endpoint = process.env.VUE_APP_OBSERVABILITY_RUM_URL || '/observability/rum'

  const span = trace.getSpan(context.active())
  const spanContext = span && span.spanContext()
  const payload = JSON.stringify({
    schema_version: 1,
    event: name,
    timestamp: new Date().toISOString(),
    service: SERVICE_NAME,
    version: process.env.VUE_APP_RELEASE || 'unknown',
    environment: process.env.VUE_APP_ENV || 'unknown',
    route: normalizedPath(window.location.href),
    trace_id: spanContext && /^[a-f0-9]{32}$/i.test(spanContext.traceId || '') ? spanContext.traceId : undefined,
    attributes
  })
  if (payload.length > MAX_EVENT_BYTES) return

  if (navigator.sendBeacon) {
    navigator.sendBeacon(endpoint, new Blob([payload], { type: 'application/json' }))
    return
  }
  if (typeof fetch !== 'function') return
  fetch(endpoint, { method: 'POST', body: payload, headers: { 'content-type': 'application/json' }, keepalive: true, credentials: 'omit' }).catch(() => {})
}

export default function setupObservability(router) {
  if (process.env.VUE_APP_OBSERVABILITY_ENABLED === 'false') return
  if (configured) return
  configured = true

  const traceURL = process.env.VUE_APP_OBSERVABILITY_TRACES_URL || '/observability/v1/traces'
  if (traceURL) {
    const resource = new Resource({
      'service.name': SERVICE_NAME,
      'service.namespace': 'simpanforbiz',
      'service.version': process.env.VUE_APP_RELEASE || 'unknown',
      'deployment.environment.name': process.env.VUE_APP_ENV || 'unknown'
    })
    const exporter = new OTLPTraceExporter({ url: traceURL })
    const configuredRatio = Number(process.env.VUE_APP_TRACE_SAMPLE_RATIO || '0.2')
    const ratio = Number.isFinite(configuredRatio) && configuredRatio >= 0 && configuredRatio <= 1 ? configuredRatio : 0.2
    const provider = new WebTracerProvider({ resource, sampler: new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(ratio) }) })
    provider.addSpanProcessor(new BatchSpanProcessor(exporter))
    provider.register({ contextManager: new ZoneContextManager() })

    const targets = propagationTargets()
    registerInstrumentations({
      instrumentations: [
        new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls: targets, ignoreUrls: [/\/observability\//, /sentry\.io/] }),
        new FetchInstrumentation({ propagateTraceHeaderCorsUrls: targets, ignoreUrls: [/\/observability\//, /sentry\.io/], clearTimingResources: true })
      ]
    })

    const tracer = provider.getTracer(SERVICE_NAME)
    router.afterEach((to, from) => {
      const span = tracer.startSpan('route change', { attributes: { 'route.to': normalizedPath(to.fullPath), 'route.from': normalizedPath(from.fullPath) } })
      span.end()
      recordRumEvent('route_change', { from: normalizedPath(from.fullPath), to: normalizedPath(to.fullPath) })
    })
  }

  const reportVital = ({ name, value, rating }) => recordRumEvent('web_vital', { name, value: Math.round(value * 1000) / 1000, rating })
  onCLS(reportVital)
  onINP(reportVital)
  onLCP(reportVital)
  window.addEventListener('error', () => recordRumEvent('browser_error'))
  window.addEventListener('unhandledrejection', () => recordRumEvent('unhandled_rejection'))
}
